/**
 * useWorkspace hook
 * Task 27: Updated to load tenant configuration
 */

import { useCallback, useEffect } from 'react';
import { useWorkspaceStore } from '../stores/workspace';
import { getAPIClient } from '../api/client';
import { getTenantConfig } from '../api/tenant-config';
import type { TenantDeploymentConfig } from '../types/tenant-config';

export function useWorkspace() {
  const store = useWorkspaceStore();

  // Load tenant config when tenant changes
  const loadTenantConfig = useCallback(async (tenantId: string) => {
    store.setTenantConfigLoading(true);
    store.setTenantConfigError(null);
    
    try {
      const config = await getTenantConfig(tenantId);
      store.setTenantConfig(config);
      
      if (import.meta.env.DEV) {
        if (config) {
          console.log('[useWorkspace] Tenant config loaded:', {
            deploymentType: config.deploymentType,
            resourceStatus: config.resourceStatus,
            isActive: config.isActive,
          });
        } else {
          console.log('[useWorkspace] No custom config, tenant uses core services');
        }
      }
    } catch (error: any) {
      console.error('[useWorkspace] Failed to load tenant config:', error);
      store.setTenantConfigError(error.message || 'Failed to load tenant configuration');
      store.setTenantConfig(null);
    }
  }, [store]);

  // Sync tenant ID with API client and load config
  const selectTenant = useCallback((tenant: any) => {
    store.setTenant(tenant);
    const client = getAPIClient();
    client.setTenantId(tenant.id);
    
    // Load tenant config
    loadTenantConfig(tenant.id);
  }, [store, loadTenantConfig]);

  // Reload tenant config
  const reloadTenantConfig = useCallback(async () => {
    if (store.tenantId) {
      await loadTenantConfig(store.tenantId);
    }
  }, [store.tenantId, loadTenantConfig]);

  // Check if tenant uses custom resources
  const usesCustomResources = useCallback((resource?: 'database' | 'storage' | 'redis' | 'openai'): boolean => {
    if (!store.tenantConfig || !store.tenantConfig.isActive) {
      return false;
    }
    
    if (!resource) {
      // Check if any resource is custom
      const status = store.tenantConfig.resourceStatus;
      return status?.database === 'custom' || 
             status?.storage === 'custom' || 
             status?.redis === 'custom' || 
             status?.openai === 'custom';
    }
    
    return store.tenantConfig.resourceStatus?.[resource] === 'custom';
  }, [store.tenantConfig]);

  // Get deployment type
  const getDeploymentType = useCallback((): string => {
    return store.tenantConfig?.deploymentType || 'shared';
  }, [store.tenantConfig]);

  return {
    ...store,
    selectTenant,
    reloadTenantConfig,
    usesCustomResources,
    getDeploymentType,
    
    // Computed values
    hasCustomConfig: !!store.tenantConfig?.isActive,
    deploymentType: store.tenantConfig?.deploymentType || 'shared',
    cloudProvider: store.tenantConfig?.cloudProvider || null,
  };
}


